﻿import { Box, BoxProps } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { FunctionComponent } from "react";

// Define the shimmer animation
const shimmer = keyframes`
  0%
   {
    transform: translateX(-100%);
  }
  50%,100% {
    transform: translateX(100%);
  }
`;

export interface ShimmerProps {
    mode?: "light" | "dark";
    isLoading?: boolean;
}

/**
 * Use this to build skeletons of your pages when they are loading.
 * Wrap each component that is part of the skeleton with this component.
 *
 * ```
 * <Shimmer isLoading={query.isLoading}>
 *   <Box>Some content</Box>
 * </Shimmer>
 * ```
 *
 * @param mode
 * @param isLoading
 */
export const Shimmer: FunctionComponent<ShimmerProps & BoxProps> = ({ mode = "light", isLoading = true, children, ...props }) => {
    const color = mode === "dark" ? "255, 255, 255" : "0, 0, 0";
    const intensity = mode === "dark" ? ".2" : ".08";
    const bgIntensity = mode === "dark" ? ".1" : ".04";
    return isLoading ? (
        <Box
            sx={{
                position: "relative",
                visibility: "hidden",
                overflow: "hidden",
                "&:after": {
                    position: "absolute",
                    visibility: "visible",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    transform: "translateX(-100%)",
                    backgroundImage: `linear-gradient(90deg, rgba(${color}, 0) 0%, rgba(${color}, ${intensity}) 40%, rgba(${color}, 0) 100%)`,
                    animation: `${shimmer} 1.5s infinite linear`,
                    content: "''",
                },
                "&:before": {
                    position: "absolute",
                    visibility: "visible",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: `rgba(${color}, ${bgIntensity})`,
                    content: "''",
                },
            }}
            height={children ? "fit-content" : "1rem"}
            borderRadius={"sm"}
            {...props}
        >
            {children}
        </Box>
    ) : (
        <>{children}</>
    );
};
